import { Component, OnInit, Signal, effect, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { StateService } from "projects/shared-state/lib/state.service";
import { WorkflowState } from "projects/shared-state/public-api";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { PromptConfig, Workflow } from "projects/shared-state/lib/types/types";
import { StateModule } from "projects/shared-state/lib/state.module";

import { EditorModule, TINYMCE_SCRIPT_SRC } from "@tinymce/tinymce-angular";
import { MatDialog } from "@angular/material/dialog";
import { RegenerateDialogComponent } from "./regenerate-dialog/regenerate-dialog.component";
import { asapScheduler } from "rxjs";

@Component({
  selector: "postgenie-result-editing",
  standalone: true,
  imports: [
    CommonModule,
    StateModule,
    FormsModule,
    ReactiveFormsModule,
    EditorModule,
  ],
  providers: [
    { provide: TINYMCE_SCRIPT_SRC, useValue: "tinymce/tinymce.min.js" },
  ],
  templateUrl: "./result-editing.component.html",
  styleUrls: ["./result-editing.component.scss"],
})
export class ResultEditingComponent implements OnInit {
  stateService = inject(StateService);
  dialog = inject(MatDialog);

  stateSignal$: Signal<{
    workflow: WorkflowState;
  }> = this.stateService.getAppState();

  workflowForm: FormGroup = new FormGroup({
    postFinal: new FormControl("", []),
  });

  public disabled$: boolean = true;

  constructor() {
    effect(() => {
      const workflow: Workflow = this.stateSignal$().workflow.workflow;
      const error: boolean = this.stateSignal$().workflow.error;
      if (workflow.promptWorkflow.aiResults?.postFinalEditing != "") {
        this.workflowForm.patchValue({
          postFinal: workflow.promptWorkflow.aiResults?.postFinalEditing,
        });
        this.disabled$ = false;
      } else {
        if (error) {
          this.handleError();
        }
      }
    });
  }

  ngOnInit(): void {
    window.onbeforeunload = () => this.ngOnDestroy();
  }

  getTinyMCEConfig(): object {
    return {
      base_url: "/tinymce",
      suffix: ".min",
      branding: false,
      promotion: false,
      autoresize_max_height: 500,
      menubar: "edit view insert format tools",
      plugins: [
        "advlist",
        "emoticons",
        "autolink",
        "autoresize",
        "lists",
        "link",
        "charmap",
        "searchreplace",
        "code",
        "wordcount",
      ],
      toolbar:
        "regenerateText | emoticons | undo redo | " +
        "bold italic | alignleft aligncenter " +
        "alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat",
      content_style:
        "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
      setup: (editor) => {
        editor.ui.registry.addButton("regenerateText", {
          text: "Improve selected text",
          icon: "line",
          tooltip:
            "Select text area and ask AI to rewrite it based on your instructions",
          onAction: () => {
            let selection = editor.selection.getContent();
            if (selection) {
              this.improveMarkedText(selection);
            }
          },
        });
      },
    };
  }

  improveMarkedText(markedText): void {
    const dialogRef = this.dialog.open(RegenerateDialogComponent, {
      backdropClass: "backdropBackground",
      // position: {
      //   top: `${button.offsetTop}px`,
      //   left: `${button.offsetLeft}px`
      // }
    });

    dialogRef.afterClosed().subscribe((postTone) => {
      if (postTone) {
        this.askGenAi(markedText, postTone);
      }
    });
  }

  askGenAi(markedText, postTone): void {
    const promptConfiguration: PromptConfig = {
      promptIdentifier: "post_adjustments",
      replacementDict: {
        post: this.workflowForm.value.postFinal,
        replacement: markedText,
        tone: postTone,
        profileSummary:
          this.stateSignal$().workflow.profile.appProfile.profileSummary || "",
      },
    };
    this.saveWorkflow();
    this.stateService.askGenAiApi(
      promptConfiguration,
      "postFinalEditing",
      "postProcess",
      0.6,
    );
    this.disabled$ = true;
  }

  saveWorkflow(postFinalEditiing: string = ""): void {
    this.stateService.saveWorkflow(
      {
        ...this.stateSignal$().workflow.workflow,
        promptWorkflow: {
          userInputs: {
            ...this.stateSignal$().workflow.workflow.promptWorkflow.userInputs,
          },
          aiResults: {
            ...this.stateSignal$().workflow.workflow.promptWorkflow.aiResults,
            postFinalEditing: postFinalEditiing,
          },
          uploadedFiles: [
            ...this.stateSignal$().workflow.workflow.promptWorkflow
              .uploadedFiles,
          ],
        },
      },
      this.stateSignal$().workflow.workflow.uid,
    );
  }

  ngOnDestroy(): void {
    this.saveWorkflow(this.workflowForm.value.postFinal);
  }

  handleError(): void {
    this.disabled$ = false;
    asapScheduler.schedule(() => {
      this.stateService.updateErrorState(false);
    });
  }
}
